/**
 * Docling Configuration API Client
 * Handles Docling SaaS API key configuration per user.
 */

import { apiClient } from './client';
import type { DoclingConfig } from '@snxt/snxt-onto-share';

export interface SaveDoclingConfigRequest {
  apiKey: string;
  saasUrl?: string;
}

export interface TestDoclingKeyRequest {
  apiKey: string;
  saasUrl?: string;
}

export interface TestDoclingKeyResponse {
  isValid: boolean;
  error?: string;
}

// ── Platform override status ──────────────────────────────────────────────────

export interface DoclingOverrideStatus {
  /** True when the backend has a deployment-wide Docling SaaS key configured. */
  overrideActive: boolean;
  /** The SaaS endpoint URL used by the override (for display purposes only). */
  saasUrl?: string;
  /** Human-readable label set by the admin (e.g. "IBM Docling SaaS"). */
  displayName?: string;
}

export const doclingConfigApi = {
  /**
   * Get the current user's Docling configuration (key is masked).
   * Returns null for 404 (no config yet) or 5xx (e.g. table not yet migrated on
   * the deployed environment), so the panel always shows the unconfigured form
   * rather than a hard error banner. Save/update errors surface through their
   * own mutations.
   */
  async getConfig(): Promise<DoclingConfig | null> {
    try {
      return await apiClient.get<DoclingConfig>('/api/docling-config');
    } catch (err: any) {
      if (err?.status === 404 || (err?.status && err.status >= 500)) return null;
      throw err;
    }
  },

  /**
   * Save (create or update) the user's Docling SaaS API key.
   */
  async saveConfig(data: SaveDoclingConfigRequest): Promise<DoclingConfig> {
    return apiClient.put<DoclingConfig>('/api/docling-config', data);
  },

  /**
   * Delete the user's Docling SaaS API key (revert to local conversion).
   */
  async deleteConfig(): Promise<void> {
    return apiClient.delete('/api/docling-config');
  },

  /**
   * Test whether the provided API key can reach the Docling SaaS service.
   */
  async testKey(data: TestDoclingKeyRequest): Promise<TestDoclingKeyResponse> {
    return apiClient.post<TestDoclingKeyResponse>('/api/docling-config/test', data);
  },

  /**
   * Get the platform-global Docling SaaS override status.
   * When overrideActive=true the per-user DoclingConfigurationPanel should be
   * replaced by a read-only "Platform Global Setting" banner.
   */
  async getOverrideStatus(): Promise<DoclingOverrideStatus> {
    return apiClient.get<DoclingOverrideStatus>('/api/docling-config/status');
  },
};

// Made with Bob
